import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatMoney, formatDate, todayISO } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import DatePicker from "@/components/DatePicker";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  TrendingUp,
  Wallet,
  Receipt,
  Users,
  Package,
  ArrowUpRight,
  ArrowDownRight,
  Crown,
  ChevronRight,
  ShieldOff,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

function KpiCard({ label, value, icon: Icon, accent, testid, footer }) {
  return (
    <Card
      className="p-5 border-[#E5E0D8] bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow"
      data-testid={testid}
    >
      <div className="flex items-start justify-between mb-4">
        <div
          className="h-10 w-10 rounded-lg flex items-center justify-center"
          style={{ background: accent + "1A", color: accent }}
        >
          <Icon className="h-5 w-5" />
        </div>
        <ArrowUpRight className="h-4 w-4 text-[#8C8C8C]" />
      </div>
      <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#8C8C8C] mb-1">
        {label}
      </div>
      <div className="font-display text-3xl font-bold text-[#1A1A1A] leading-tight">
        {value}
      </div>
      {footer && (
        <div className="text-[12px] text-[#595959] mt-2">{footer}</div>
      )}
    </Card>
  );
}

export default function Dashboard() {
  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (from) params.desde = from;
      if (to) params.hasta = to;
      const res = await api.get("/dashboard/kpis", { params });
      setData(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [from, to]);

  const ventas = useMemo(
    () => (data?.ventas_por_fecha || []).map((v) => ({ ...v, label: v.fecha.slice(5) })),
    [data]
  );

  return (
    <div>
      <PageHeader
        eyebrow="Resumen del negocio"
        title="Dashboard"
        subtitle="Indicadores clave de ventas, clientes y costos del período seleccionado."
      >
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-[11px] text-[#595959] mb-1.5 block">Desde</Label>
            <DatePicker value={from} onChange={setFrom} testid="dash-from" className="w-[160px]" size="sm" />
          </div>
          <div>
            <Label className="text-[11px] text-[#595959] mb-1.5 block">Hasta</Label>
            <DatePicker value={to} onChange={setTo} testid="dash-to" className="w-[160px]" size="sm" />
          </div>
        </div>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KpiCard
          label="Total Vendido"
          value={formatMoney(data?.total_vendido)}
          icon={Wallet}
          accent="#C85A32"
          testid="kpi-total-vendido"
          footer={`${data?.total_pedidos || 0} pedidos confirmados`}
        />
        <KpiCard
          label="Ticket Promedio"
          value={formatMoney(data?.ticket_promedio)}
          icon={Receipt}
          accent="#6B705C"
          testid="kpi-ticket-promedio"
        />
        <KpiCard
          label="Productos Vendidos"
          value={data?.productos_vendidos ?? 0}
          icon={Package}
          accent="#D49A36"
          testid="kpi-productos-vendidos"
        />
        <KpiCard
          label="Clientes Activos"
          value={data?.clientes_activos ?? 0}
          icon={Users}
          accent="#2C2A26"
          testid="kpi-clientes-activos"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <Card className="lg:col-span-2 p-6 border-[#E5E0D8] rounded-2xl bg-white" data-testid="card-ventas-chart">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#8C8C8C]">
                Ventas por fecha
              </div>
              <div className="font-display text-xl font-bold text-[#1A1A1A]">
                {formatMoney(data?.total_vendido)}
              </div>
            </div>
            <TrendingUp className="h-5 w-5 text-[#6B705C]" />
          </div>
          <div className="h-[280px] -ml-2">
            {ventas.length === 0 ? (
              <div className="h-full flex items-center justify-center text-[#8C8C8C] text-sm">
                {loading ? "Cargando..." : "Sin ventas en el período."}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ventas} margin={{ top: 12, right: 12, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" vertical={false} />
                  <XAxis dataKey="label" tick={{ fill: "#595959", fontSize: 12 }} axisLine={{ stroke: "#E5E0D8" }} tickLine={false} />
                  <YAxis tick={{ fill: "#595959", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <Tooltip
                    cursor={{ fill: "rgba(200,90,50,0.06)" }}
                    contentStyle={{
                      background: "#fff",
                      border: "1px solid #E5E0D8",
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                    formatter={(v) => formatMoney(v)}
                    labelFormatter={(l) => `Fecha: ${l}`}
                  />
                  <Bar dataKey="total" fill="#C85A32" radius={[6, 6, 0, 0]} maxBarSize={36} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card className="p-6 border-[#E5E0D8] rounded-2xl bg-white" data-testid="card-gastos">
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#8C8C8C] mb-1">
            Gastos en insumos
          </div>
          <div className="font-display text-3xl font-bold text-[#1A1A1A] mb-1">
            {formatMoney(data?.total_gastado)}
          </div>
          <div className="text-sm text-[#595959] mb-6">en el período seleccionado</div>

          <div className="space-y-3 pt-4 border-t border-[#E5E0D8]">
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#8C8C8C]">
              Ganancia estimada
            </div>
            <div className="flex items-center gap-2">
              {(data?.ganancia_estimada ?? 0) >= 0 ? (
                <ArrowUpRight className="h-5 w-5 text-[#6B705C]" />
              ) : (
                <ArrowDownRight className="h-5 w-5 text-[#B23A3A]" />
              )}
              <div className={`font-display text-2xl font-bold ${(data?.ganancia_estimada ?? 0) >= 0 ? "text-[#6B705C]" : "text-[#B23A3A]"}`}>
                {formatMoney(data?.ganancia_estimada)}
              </div>
            </div>
            <div className="text-xs text-[#8C8C8C]">Ventas - Gastos en insumos</div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <RankingCard
          title="Top productos por cantidad"
          rows={data?.top_productos_cantidad || []}
          unit="u"
          accent="#C85A32"
          testid="rank-prod-cant"
        />
        <RankingCard
          title="Top productos por facturación"
          rows={data?.top_productos_facturacion || []}
          money
          accent="#6B705C"
          testid="rank-prod-fact"
        />
      </div>

      <RankingClientesCard />
    </div>
  );
}

function RankingCard({ title, rows, unit, money, accent, testid }) {
  return (
    <Card className="p-6 border-[#E5E0D8] rounded-2xl bg-white" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#8C8C8C] mb-4">
        {title}
      </div>
      {rows.length === 0 ? (
        <div className="text-sm text-[#8C8C8C] py-6">Sin datos en el período.</div>
      ) : (
        <ul className="space-y-3">
          {rows.slice(0, 7).map((r, i) => (
            <li key={r.nombre + i} className="flex items-center gap-3 fade-row" style={{ animationDelay: `${i * 40}ms` }}>
              <div
                className="h-7 w-7 rounded-md flex items-center justify-center text-[12px] font-bold"
                style={{ background: accent + "14", color: accent }}
              >
                {i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[#1A1A1A] truncate font-medium">{r.nombre}</div>
              </div>
              <div className="text-sm font-mono text-[#1A1A1A] font-semibold">
                {money ? formatMoney(r.valor) : `${r.valor} ${unit || ""}`}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}


// ============================================================================
// Ranking de Clientes — full table with quick range chips + sort + Ver todos
// ============================================================================

const RANGE_PRESETS = [
  { key: "7d",   label: "7 días",    days: 7 },
  { key: "30d",  label: "30 días",   days: 30 },
  { key: "90d",  label: "90 días",   days: 90 },
  { key: "mes",  label: "Este mes",  days: null },
  { key: "todo", label: "Todo",      days: null },
];

const SORT_OPTIONS = [
  { value: "total",   label: "Total gastado" },
  { value: "pedidos", label: "Cantidad de pedidos" },
  { value: "deuda",   label: "Deuda" },
  { value: "ticket",  label: "Ticket promedio" },
  { value: "ultima",  label: "Última compra" },
];

function isoDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}
function firstDayOfMonthISO() {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
}

function RankingClientesCard() {
  const navigate = useNavigate();
  const [preset, setPreset] = useState("30d");
  const [from, setFrom] = useState(isoDaysAgo(30));
  const [to, setTo] = useState(todayISO());
  const [sort, setSort] = useState("total");
  const [rows, setRows] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [error, setError] = useState("");

  // When user picks a preset, recompute from/to
  const applyPreset = (key) => {
    setPreset(key);
    if (key === "7d")  { setFrom(isoDaysAgo(7));  setTo(todayISO()); }
    if (key === "30d") { setFrom(isoDaysAgo(30)); setTo(todayISO()); }
    if (key === "90d") { setFrom(isoDaysAgo(90)); setTo(todayISO()); }
    if (key === "mes") { setFrom(firstDayOfMonthISO()); setTo(todayISO()); }
    if (key === "todo"){ setFrom(""); setTo(""); }
  };

  const load = async () => {
    setLoading(true); setError("");
    try {
      const params = { sort, limit: showAll ? 500 : 20 };
      if (from) params.desde = from;
      if (to)   params.hasta = to;
      const r = await api.get("/dashboard/ranking-clientes", { params });
      setRows(r.data.rows || []);
      setResumen(r.data.resumen || null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error al cargar ranking");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [from, to, sort, showAll]); // eslint-disable-line react-hooks/exhaustive-deps

  const goToCliente = (cid) => {
    if (!cid) return;
    navigate(`/admin/cuenta-corriente/${cid}`);
  };

  return (
    <Card className="p-4 sm:p-6 border-[#E5E0D8] rounded-2xl bg-white" data-testid="rank-clientes-full">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <div className="h-10 w-10 rounded-lg bg-[#F4EAE6] text-[#C85A32] flex items-center justify-center shrink-0">
            <Crown className="h-5 w-5" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#8C8C8C]">
              Ranking de clientes
            </div>
            <div className="font-display font-bold text-xl text-[#1A1A1A] leading-tight">
              ¿A quién priorizar?
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-[11px] text-[#595959] hidden sm:inline">Ordenar por</Label>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-[180px] h-9 text-[13px]" data-testid="rank-sort">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`rank-sort-${o.value}`}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Range presets */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {RANGE_PRESETS.map((r) => (
          <button
            key={r.key}
            type="button"
            onClick={() => applyPreset(r.key)}
            className={`px-3 py-1.5 rounded-full text-[12px] font-semibold transition-colors ${
              preset === r.key
                ? "bg-[#C85A32] text-white shadow-sm"
                : "bg-white text-[#595959] border border-[#E5E0D8] hover:border-[#C85A32] hover:text-[#C85A32]"
            }`}
            data-testid={`rank-range-${r.key}`}
          >
            {r.label}
          </button>
        ))}
        <span className="text-[11px] text-[#8C8C8C] ml-2 self-center">
          {from || to ? `${from || "…"} → ${to || "…"}` : "Todo el histórico"}
        </span>
      </div>

      {/* Table */}
      {loading ? (
        <div className="py-10 text-center text-[#8C8C8C]">Cargando ranking...</div>
      ) : error ? (
        <div className="py-6 text-center text-[#B23A3A] text-sm">{error}</div>
      ) : rows.length === 0 ? (
        <div className="py-10 text-center text-[#8C8C8C] text-sm">
          Sin clientes con actividad en el período seleccionado.
        </div>
      ) : (
        <>
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <table className="w-full text-left min-w-[720px]">
              <thead className="border-b border-[#E5E0D8]">
                <tr className="text-[10px] uppercase tracking-[0.14em] text-[#8C8C8C]">
                  <th className="px-3 py-2 w-10">#</th>
                  <th className="px-3 py-2">Cliente</th>
                  <th className="px-3 py-2 text-right">Pedidos</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2 text-right">Ticket prom.</th>
                  <th className="px-3 py-2">Última compra</th>
                  <th className="px-3 py-2 text-right">Deuda</th>
                  <th className="px-3 py-2 w-6"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const clickable = !!r.cliente_id;
                  const suspended = r.account_status === "SUSPENDED";
                  return (
                    <tr
                      key={(r.cliente_id || "legacy") + "-" + i}
                      className={`border-b border-[#F0EBE0] fade-row ${
                        clickable ? "hover:bg-[#FBF8F2] cursor-pointer" : "opacity-70"
                      }`}
                      style={{ animationDelay: `${Math.min(i, 20) * 20}ms` }}
                      onClick={() => clickable && goToCliente(r.cliente_id)}
                      data-testid={`rank-row-${i}`}
                    >
                      <td className="px-3 py-3">
                        <span
                          className={`inline-flex items-center justify-center w-6 h-6 rounded-md text-[11px] font-bold ${
                            i === 0 ? "bg-[#D49A36] text-white" :
                            i === 1 ? "bg-[#C0C0C0] text-white" :
                            i === 2 ? "bg-[#CD7F32] text-white" :
                                      "bg-[#F9F6F0] text-[#595959]"
                          }`}
                        >
                          {i + 1}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="min-w-0">
                            <div className="font-semibold text-[13px] text-[#1A1A1A] truncate flex items-center gap-2">
                              {r.nombre}
                              {suspended && (
                                <span
                                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-[#FBEBEB] text-[#8E2E2E] border border-[#E8B5B5]"
                                  data-testid={`rank-row-${i}-suspended`}
                                >
                                  <ShieldOff className="h-2.5 w-2.5" />
                                  Susp.
                                </span>
                              )}
                            </div>
                            {r.email && (
                              <div className="text-[11px] text-[#8C8C8C] truncate font-mono">{r.email}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right font-mono text-[13px] text-[#1A1A1A]">
                        {r.cantidad_pedidos}
                      </td>
                      <td className="px-3 py-3 text-right font-mono font-bold text-[13px] text-[#C85A32]">
                        {formatMoney(r.total_gastado)}
                      </td>
                      <td className="px-3 py-3 text-right font-mono text-[12px] text-[#595959]">
                        {formatMoney(r.ticket_promedio)}
                      </td>
                      <td className="px-3 py-3 text-[12px] text-[#595959]">
                        {r.ultima_compra ? formatDate(r.ultima_compra) : "—"}
                      </td>
                      <td className="px-3 py-3 text-right">
                        {r.deuda_total > 0 ? (
                          <span className="font-mono font-bold text-[13px] text-[#A64A29]">
                            {formatMoney(r.deuda_total)}
                          </span>
                        ) : (
                          <span className="text-[12px] text-[#8C8C8C]">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-[#8C8C8C]">
                        {clickable && <ChevronRight className="h-4 w-4" />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {resumen && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#E5E0D8] flex-wrap gap-3">
              <div className="text-[12px] text-[#595959]">
                Mostrando <b>{rows.length}</b> de <b>{resumen.total_clientes}</b> clientes ·
                Deuda global histórica: <b className="text-[#A64A29]">{formatMoney(resumen.deuda_total_global)}</b>
              </div>
              {resumen.total_clientes > rows.length && !showAll && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAll(true)}
                  className="border-[#C85A32] text-[#C85A32] hover:bg-[#F4EAE6]"
                  data-testid="rank-ver-todos"
                >
                  Ver todos ({resumen.total_clientes})
                </Button>
              )}
              {showAll && rows.length > 20 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAll(false)}
                  data-testid="rank-ver-top20"
                >
                  Ver top 20
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </Card>
  );
}
